/**
* Autocollector.ts
* @author Abhilash Panwar (abpanwar) Hector Hernandez (hectorh)
* @copyright Microsoft 2020
* File containing class for autocollecting signals for EI.
*/
import { Utils, getLocation, getDocument, isDocumentObjectAvailable, getWindow, isWindowObjectAvailable } from '@microsoft/1ds-core-js';
import { _isRightClick, _isLeftClick, _isMiddleClick, _isKeyboardEnter, _isKeyboardSpace, _clickCaptureInputTypes, _getClickTarget, _getContent, } from './AnalyticsHelper';
import { ActionType } from './Enums';
import { _emptyString } from '../properties/Constants';
var clickCaptureElements = { A: true, BUTTON: true, AREA: true, INPUT: true };
/**
 * Class for autocollecting signals.
 */
var Autocollector = /** @class */ (function () {
    function Autocollector() {
    }
    /**
     * Autocollects signals based on the config passed.
     * @param analytics The analytics class to use to send signals.
     * @param config The config detailing what needs to be auto collected.
     */
    Autocollector.prototype._autocollect = function (analytics, config) {
        this._analytics = analytics;
        var captureView = this._captureView.bind(this);
        if (config && Utils.isObject(config)) {
            if (config.view) {
                captureView();
                var win_1 = getWindow();
                var _replaceState_1 = win_1.history.replaceState;
                var _pushState_1 = win_1.history.pushState;
                win_1.history.replaceState = function () {
                    _replaceState_1.apply(win_1.history, arguments);
                    captureView();
                };
                win_1.history.pushState = function () {
                    _pushState_1.apply(win_1.history, arguments);
                    captureView();
                };
            }
            if (config.click) {
                this._captureClicks();
            }
        }
    };
    /**
     * Gets the screen resolution.
     */
    Autocollector.prototype._getScreenResolution = function () {
        if (isDocumentObjectAvailable && isWindowObjectAvailable) {
            var win = getWindow();
            var doc = getDocument();
            return {
                h: screen.height,
                w: screen.width,
                cd: screen.colorDepth,
                vW: Math.max(doc.documentElement.clientWidth, win.innerWidth || 0),
                vH: Math.max(doc.documentElement.clientHeight, win.innerHeight || 0),
                sR: screen.height && screen.width ? screen.height + 'X' + screen.width : _emptyString
            };
        }
        return null;
    };
    Autocollector.prototype._getPageNameFromPath = function (pageName) {
        pageName = pageName.replace(/\/$/, "");
        var fragments = pageName.split('/');
        if (fragments && fragments[fragments.length - 1] !== _emptyString) {
            pageName = fragments[fragments.length - 1];
        }
        else {
            pageName = '/';
        }
        return pageName;
    };
    Autocollector.prototype._getReferrer = function (doc) {
        return {
            uri: doc.referrer
        };
    };
    Autocollector.prototype._removeAllListeners = function () {
        var _this = this;
        var win = getWindow();
        var doc = getDocument();
        if (win && win.addEventListener) {
            // IE9 onwards addEventListener is available, 'click' event captures mouse click. mousedown works on other browsers
            var event = (navigator.appVersion.indexOf('MSIE') !== -1) ? 'click' : 'mousedown';
            win.removeEventListener(event, function (evt) { _this._processClick(evt); }, false);
            win.removeEventListener('keyup', function (evt) { _this._processClick(evt); }, false);
        }
    };
    Autocollector.prototype._captureView = function () {
        var loc = getLocation();
        if (isDocumentObjectAvailable && loc) {
            var doc = getDocument();
            var view = {
                uri: loc.href,
                title: doc.title.substring(0, 150),
                name: this._getPageNameFromPath(loc.pathname || _emptyString),
                referrer: this._getReferrer(doc)
            };
            this._analytics.trackView(view);
        }
    };
    Autocollector.prototype._captureClicks = function () {
        var _this = this;
        var win = getWindow();
        var doc = getDocument();
        if (win && win.addEventListener) {
            // IE9 onwards addEventListener is available, 'click' event captures mouse click. mousedown works on other browsers
            var event = (navigator.appVersion.indexOf('MSIE') !== -1) ? 'click' : 'mousedown';
            win.addEventListener(event, function (evt) { _this._processClick(evt); }, false);
            win.addEventListener('keyup', function (evt) { _this._processClick(evt); }, false);
        }
    };
    Autocollector.prototype._processClick = function (clickEvent) {
        var element = clickEvent.srcElement || clickEvent.target;
        var sendAction = false;
        // Check if we should capture the element
        while (element && element.tagName) {
            if (element.control && clickCaptureElements[element.control.tagName.toUpperCase()]) {
                element = element.control;
            }
            if (!clickCaptureElements[element.tagName.toUpperCase()]) {
                element = element.parentElement || element.parentNode;
                continue;
            }
            else {
                // Check allowed INPUT types
                sendAction = element.tagName.toUpperCase() === 'INPUT' ? _clickCaptureInputTypes[element.type.toUpperCase()] : true;
                break;
            }
        }
        if (sendAction) {
            // Capture the action type
            var actionType = void 0;
            if (_isRightClick(clickEvent)) {
                actionType = ActionType.rightClick;
            }
            else if (_isLeftClick(clickEvent)) {
                actionType = ActionType.leftClick;
            }
            else if (_isKeyboardEnter(clickEvent)) {
                actionType = ActionType.keyboardEnter;
            }
            else if (_isKeyboardSpace(clickEvent)) {
                actionType = ActionType.keyboardSpace;
            }
            else if (_isMiddleClick(clickEvent)) {
                actionType = ActionType.middleClick;
            }
            else {
                return;
            }
            var content = _getContent(element);
            var action = {
                name: content.n,
                type: actionType,
                clickCoordinates: clickEvent.pageX + 'X' + clickEvent.pageY,
                "class": content.c,
                elementId: content.id,
                viewTarget: _getClickTarget(element)
            };
            this._analytics.trackAction(action);
        }
    };
    return Autocollector;
}());
export default Autocollector;
//# sourceMappingURL=Autocollector.js.map